import { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton } from '@mui/material';
// components
import { useSnackbar } from 'notistack';
import Iconify from './Iconify';

// ----------------------------------------------------------------------
export default function RecorderPlayer({ onSend, onCancel }) {
  const { enqueueSnackbar } = useSnackbar();
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    startRecording();
    return () => {
      isMountedRef.current = false;
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.onstop = null; // evita callback após desmontagem
        mediaRecorderRef.current.stop();
      }
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopStream = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const workerOptions = {
        OggOpusEncoderWasmPath: 'https://cdn.jsdelivr.net/npm/opus-media-recorder@latest/OggOpusEncoder.wasm',
        WebMOpusEncoderWasmPath: 'https://cdn.jsdelivr.net/npm/opus-media-recorder@latest/WebMOpusEncoder.wasm',
      };

      const RecorderClass = window.OpusMediaRecorder || MediaRecorder;
      const recorder = new RecorderClass(stream, { mimeType: 'audio/ogg;codecs=opus' }, workerOptions);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      recorder.start(500);

      timerIntervalRef.current = setInterval(() => {
        if (isMountedRef.current) setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Erro ao acessar o microfone:', error);
      enqueueSnackbar('Não foi possível acessar o microfone. Verifique as permissões.', {
        variant: 'error',
        autoHideDuration: 5000,
      });
      onCancel();
    }
  };

  const handleSend = useCallback(() => {
    if (!mediaRecorderRef.current) return;
    clearInterval(timerIntervalRef.current);
    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(audioChunksRef.current, { type: 'audio/ogg;codecs=opus' });
      const audioFile = new File([blob], 'recorded_audio.ogg', { type: 'audio/ogg;codecs=opus' });
      audioFile.attachmentDuration = recordingTime;
      audioFile.preview = URL.createObjectURL(blob);
      stopStream();
      if (isMountedRef.current) onSend(audioFile);
    };
    mediaRecorderRef.current.stop();
  }, [recordingTime, onSend]);

  const handleCancel = useCallback(() => {
    clearInterval(timerIntervalRef.current);
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
    stopStream();
    onCancel();
  }, [onCancel]);

  const minutes = Math.floor(recordingTime / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (recordingTime % 60).toString().padStart(2, '0');

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            bgcolor: 'error.main',
            borderRadius: '50%',
            mr: 1,
            animation: 'pulse 1.5s infinite ease-in-out',
            '@keyframes pulse': {
              '0%': { opacity: 1 },
              '50%': { opacity: 0.4 },
              '100%': { opacity: 1 },
            },
          }}
        />
        <Typography variant="body2" color="error">
          Gravando... {minutes}:{seconds}
        </Typography>
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      <IconButton onClick={handleCancel} color="error">
        <Iconify icon="eva:trash-2-fill" width={24} height={24} />
      </IconButton>
      <IconButton onClick={handleSend} color="primary">
        <Iconify icon="eva:paper-plane-fill" width={24} height={24} />
      </IconButton>
    </Box>
  );
}

RecorderPlayer.propTypes = {
  onSend: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
